import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function MarketingPriorities() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Budget & Timeline
  const [monthlyBudget, setMonthlyBudget] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [timeline, setTimeline] = useState('');
  const [timeCommitment, setTimeCommitment] = useState('');

  // Funnel Priority Rankings (1 = highest priority, 6 = lowest priority)
  const [funnelRankings, setFunnelRankings] = useState({
    awareness: null,
    lead_generation: null,
    nurturing: null,
    conversion: null,
    onboarding: null,
    retention: null
  });

  const funnelStages = [
    {
      key: 'awareness',
      name: 'Stage 1: Awareness & Traffic',
      description: 'Getting more people to know you exist (SEO, ads, social media, PR)'
    },
    {
      key: 'lead_generation',
      name: 'Stage 2: Lead Generation',
      description: 'Capturing contact information (opt-ins, lead magnets, forms)'
    },
    {
      key: 'nurturing',
      name: 'Stage 3: Nurturing & Engagement',
      description: 'Building trust and relationships (email sequences, content, follow-up)'
    },
    {
      key: 'conversion',
      name: 'Stage 4: Conversion & Sales',
      description: 'Turning prospects into paying customers (sales process, offers, closing)'
    },
    {
      key: 'onboarding',
      name: 'Stage 5: Onboarding & Delivery',
      description: 'Getting customers started successfully (implementation, first results)'
    },
    {
      key: 'retention',
      name: 'Stage 6: Retention & Loyalty',
      description: 'Keeping customers happy and coming back (support, upsells, community)'
    }
  ];

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load saved priorities if exists
    await fetchSavedPriorities();

    setLoading(false);
  };

  const fetchSavedPriorities = async () => {
    try {
      const response = await fetch('/api/marketing/get-priorities', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.budget) {
          setMonthlyBudget(data.budget.monthly_budget || '');
          setBudgetRange(data.budget.budget_range || '');
          setTimeline(data.budget.timeline || '');
          setTimeCommitment(data.budget.time_commitment || '');
        }
        if (data.funnel_rankings) {
          setFunnelRankings(data.funnel_rankings);
        }
      }
      // 404 is fine - no saved data yet
    } catch (err) {
      console.log('No saved priorities found');
    }
  };

  const handleRankingChange = (stageKey, rank) => {
    const newRank = parseInt(rank);

    // Check if this rank is already used
    const currentStageWithRank = Object.entries(funnelRankings).find(
      ([key, value]) => value === newRank && key !== stageKey
    );

    // If rank is already used, swap them
    if (currentStageWithRank) {
      setFunnelRankings({
        ...funnelRankings,
        [stageKey]: newRank,
        [currentStageWithRank[0]]: funnelRankings[stageKey]
      });
    } else {
      // Otherwise just set the new rank
      setFunnelRankings({
        ...funnelRankings,
        [stageKey]: newRank
      });
    }
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'danger'; // Highest priority = red
    if (rank === 2) return 'warning';
    if (rank === 3) return 'info';
    if (rank <= 6) return 'secondary';
    return 'light';
  };

  const allRanksAssigned = () => {
    return Object.values(funnelRankings).every(rank => rank !== null) &&
           Object.values(funnelRankings).length === 6 &&
           new Set(Object.values(funnelRankings)).size === 6;
  };

  const handleSaveAndContinue = async () => {
    // Validation
    if (!budgetRange) {
      setError('Please select a paid advertising budget range');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!timeline) {
      setError('Please select when you need to see marketing results');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!timeCommitment) {
      setError('Please select your time commitment');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!allRanksAssigned()) {
      setError('Please rank all 6 funnel stages (1-6, with 1 being highest priority). Make sure each stage has a unique rank.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/marketing/save-priorities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          budget: {
            monthly_budget: monthlyBudget,
            budget_range: budgetRange,
            timeline: timeline,
            time_commitment: timeCommitment
          },
          funnel_rankings: funnelRankings
        })
      });

      if (response.ok) {
        // Navigate back to dashboard
        navigate('/dashboard/marketing-strategy');
      } else {
        const errorData = await response.json();
        console.error('Save error:', errorData);
        setError(errorData.error || 'Failed to save priorities');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Error saving priorities: ' + err.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="marketing-strategy" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Marketing Priorities & Resources</h1>
              <p className="text-center text-muted mb-4">
                Set your budget, timeline, and funnel priorities to focus your marketing efforts.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {/* Section 1: Budget & Timeline */}
              <div className="mb-5">
                <h3 className="mb-3">Paid Advertising Budget & Timeline</h3>
                <p className="text-muted mb-4">
                  Understanding your paid ad budget and timeline helps us recommend realistic strategies.
                  (This is separate from your AI Profit Engineer subscription)
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>What's your monthly PAID ADVERTISING budget?</Form.Label>
                  <Form.Select
                    value={budgetRange}
                    onChange={(e) => setBudgetRange(e.target.value)}
                  >
                    <option value="">Select a range...</option>
                    <option value="$0">$0 - No paid ads (organic only)</option>
                    <option value="$0-500">$1 - $500/month</option>
                    <option value="$500-1500">$500 - $1,500/month</option>
                    <option value="$1500-3000">$1,500 - $3,000/month</option>
                    <option value="$3000-5000">$3,000 - $5,000/month</option>
                    <option value="$5000-10000">$5,000 - $10,000/month</option>
                    <option value="$10000+">$10,000+/month</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    This is for Google Ads, Facebook Ads, LinkedIn Ads, etc. - not your total marketing budget
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Specific monthly paid ad budget (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., $2,500"
                    value={monthlyBudget}
                    onChange={(e) => setMonthlyBudget(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    If you know the exact amount you plan to spend on ads, enter it here
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>When do you need to see marketing results?</Form.Label>
                  <Form.Select
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                  >
                    <option value="">Select a timeline...</option>
                    <option value="immediate">Immediately (next 30-60 days)</option>
                    <option value="1-3-months">1-3 months (quick wins)</option>
                    <option value="3-6-months">3-6 months (building momentum)</option>
                    <option value="6-12-months">6-12 months (long-term growth)</option>
                    <option value="12+-months">12+ months (strategic positioning)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    This helps us recommend tactics based on how quickly you need traction
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>How much time can you commit to marketing per week?</Form.Label>
                  <Form.Select
                    value={timeCommitment}
                    onChange={(e) => setTimeCommitment(e.target.value)}
                  >
                    <option value="">Select time commitment...</option>
                    <option value="1-5-hours">1-5 hours/week (very limited)</option>
                    <option value="5-10-hours">5-10 hours/week (part-time)</option>
                    <option value="10-20-hours">10-20 hours/week (significant)</option>
                    <option value="20+-hours">20+ hours/week (full-time focus)</option>
                    <option value="outsourcing">I plan to outsource most of it</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {/* Section 2: Funnel Priority Rankings */}
              <div className="mb-4">
                <h3 className="mb-3">Funnel Priority Rankings</h3>
                <p className="text-muted mb-4">
                  Rank these 6 funnel stages from <strong>1 (highest priority)</strong> to <strong>6 (lowest priority)</strong>.
                  Think about where your business needs the most help right now.
                </p>

                {funnelStages.map((stage) => (
                  <Card key={stage.key} className="mb-3">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col md={8}>
                          <h5 className="mb-2">
                            {stage.name}
                            {funnelRankings[stage.key] && (
                              <Badge
                                bg={getRankColor(funnelRankings[stage.key])}
                                className="ms-2"
                              >
                                Priority #{funnelRankings[stage.key]}
                              </Badge>
                            )}
                          </h5>
                          <p className="mb-0 text-muted small">{stage.description}</p>
                        </Col>
                        <Col md={4}>
                          <Form.Select
                            value={funnelRankings[stage.key] || ''}
                            onChange={(e) => handleRankingChange(stage.key, e.target.value)}
                            size="lg"
                          >
                            <option value="">Select rank...</option>
                            <option value="1">1 - Highest Priority</option>
                            <option value="2">2 - High Priority</option>
                            <option value="3">3 - Medium-High</option>
                            <option value="4">4 - Medium-Low</option>
                            <option value="5">5 - Low Priority</option>
                            <option value="6">6 - Lowest Priority</option>
                          </Form.Select>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}

                {allRanksAssigned() && (
                  <Alert variant="success" className="mt-3">
                    <strong>✓ All stages ranked!</strong> Your priority order is set.
                  </Alert>
                )}
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/marketing-strategy')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={saving}
                  onClick={handleSaveAndContinue}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue →'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default MarketingPriorities;
